/**
 * Design System - Spacing & Layout Tokens
 * 
 * Centralized spacing, sizing, and layout values for consistent spacing throughout the app.
 * Includes spacing scale, border radius, container widths, and z-index references.
 * 
 * Usage:
 *   import { spacing, borderRadius, containers } from '../design-system/tokens/spacing';
 *   <div style={{ padding: spacing[4], borderRadius: borderRadius.lg }} />
 */

import { Z_INDEX as Z_INDEX_IMPORT, getZIndex as getZIndexImport } from '../../core/utils/zIndexScale';

/**
 * Spacing Scale
 * 
 * Based on Tailwind's default spacing scale (4px base unit).
 * Use these values for padding, margins, gaps, and positioning.
 * 
 * Format: Key = spacing unit, Value = rem (and px equivalent in comment)
 */
export const spacing = {
  0: '0',                 // 0px
  px: '1px',              // 1px
  0.5: '0.125rem',        // 2px
  1: '0.25rem',           // 4px
  1.5: '0.375rem',        // 6px
  2: '0.5rem',            // 8px
  2.5: '0.625rem',        // 10px
  3: '0.75rem',           // 12px
  3.5: '0.875rem',        // 14px
  4: '1rem',              // 16px
  5: '1.25rem',           // 20px
  6: '1.5rem',            // 24px
  7: '1.75rem',           // 28px
  8: '2rem',              // 32px
  9: '2.25rem',           // 36px
  10: '2.5rem',           // 40px
  11: '2.75rem',          // 44px
  12: '3rem',             // 48px
  14: '3.5rem',           // 56px
  16: '4rem',             // 64px
  20: '5rem',             // 80px
  24: '6rem',             // 96px
  28: '7rem',             // 112px
  32: '8rem',             // 128px
  36: '9rem',             // 144px
  40: '10rem',            // 160px
  44: '11rem',            // 176px
  48: '12rem',            // 192px
  52: '13rem',            // 208px
  56: '14rem',            // 224px
  60: '15rem',            // 240px
  64: '16rem',            // 256px
  72: '18rem',            // 288px
  80: '20rem',            // 320px
  96: '24rem',            // 384px
};

/**
 * Common Spacing Patterns
 * 
 * Pre-defined spacing values for common UI patterns.
 */
export const spacingPatterns = {
  // Component internal spacing
  componentXs: spacing[2],      // 8px - Very tight spacing
  componentSm: spacing[3],      // 12px - Small component padding
  componentMd: spacing[4],      // 16px - Medium component padding (default)
  componentLg: spacing[6],      // 24px - Large component padding
  componentXl: spacing[8],      // 32px - Extra large component padding
  
  // Section/page spacing
  sectionSm: spacing[8],        // 32px - Small section gaps
  sectionMd: spacing[12],       // 48px - Medium section gaps (default)
  sectionLg: spacing[16],       // 64px - Large section gaps
  sectionXl: spacing[24],       // 96px - Extra large section gaps
  
  // Stack spacing (vertical rhythm)
  stackXs: spacing[1],          // 4px - Very tight stacks
  stackSm: spacing[2],          // 8px - Small stacks
  stackMd: spacing[4],          // 16px - Medium stacks (default)
  stackLg: spacing[6],          // 24px - Large stacks
  stackXl: spacing[8],          // 32px - Extra large stacks
  
  // Inline spacing (horizontal gaps)
  inlineXs: spacing[1],         // 4px - Very tight inline
  inlineSm: spacing[2],         // 8px - Small inline
  inlineMd: spacing[3],         // 12px - Medium inline (default)
  inlineLg: spacing[4],         // 16px - Large inline
  inlineXl: spacing[6],         // 24px - Extra large inline
};

/**
 * Border Radius Scale
 * 
 * Consistent corner rounding for cards, buttons, inputs, and other elements.
 */
export const borderRadius = {
  none: '0',                    // 0 - Sharp corners
  sm: '0.125rem',               // 2px - Subtle rounding
  base: '0.25rem',              // 4px - Default rounding
  md: '0.375rem',               // 6px - Medium rounding
  lg: '0.5rem',                 // 8px - Large rounding
  xl: '0.75rem',                // 12px - Extra large rounding
  '2xl': '1rem',                // 16px - Very large rounding
  '3xl': '1.5rem',              // 24px - Huge rounding
  full: '9999px',               // Circular/pill shape
};

/**
 * Common Border Radius Patterns
 */
export const radiusPatterns = {
  // Component types
  button: borderRadius.md,              // 6px - Buttons
  buttonLarge: borderRadius.lg,         // 8px - Large buttons
  input: borderRadius.md,               // 6px - Input fields
  card: borderRadius.lg,                // 8px - Cards
  cardLarge: borderRadius.xl,           // 12px - Large cards
  modal: borderRadius.xl,               // 12px - Modals
  badge: borderRadius.full,             // Circular - Badges
  avatar: borderRadius.full,            // Circular - Avatars
  image: borderRadius.lg,               // 8px - Images
  dropdown: borderRadius.md,            // 6px - Dropdowns
};

/**
 * Container Max-Widths
 * 
 * Consistent container widths for content areas and page layouts.
 */
export const containers = {
  sm: '640px',       // Small screens
  md: '768px',       // Medium screens
  lg: '1024px',      // Large screens
  xl: '1280px',      // Extra large screens
  '2xl': '1536px',   // 2X large screens
  '7xl': '80rem',    // 1280px - Very wide content (existing in app)
  full: '100%',      // Full width
};

/**
 * Container Patterns
 * 
 * Common container configurations for different layout needs.
 */
export const containerPatterns = {
  // Page content
  page: containers['7xl'],              // Main page content
  pageNarrow: containers.xl,            // Narrow page content (forms, articles)
  
  // Sections
  section: containers['2xl'],           // Section content
  sectionWide: containers.full,         // Full-width sections
  
  // Modals and dialogs
  modalSm: '448px',                     // Small modal (28rem)
  modalMd: '576px',                     // Medium modal (36rem)
  modalLg: '768px',                     // Large modal (48rem)
  modalXl: '1024px',                    // Extra large modal (64rem)
  
  // Panels and sidebars
  panelSm: '256px',                     // Small panel (16rem)
  panelMd: '384px',                     // Medium panel (24rem)
  panelLg: '512px',                     // Large panel (32rem)
};

/**
 * Z-Index Scale
 * 
 * Centralized z-index values for layering elements.
 * Re-exports from the existing zIndexScale.js for consistency.
 * 
 * Import the full Z_INDEX object from utils/zIndexScale.js for complete control.
 */
export const Z_INDEX = Z_INDEX_IMPORT;
export const getZIndex = getZIndexImport;

/**
 * Common Z-Index Patterns
 * 
 * Simplified z-index values for common use cases.
 */
export const zIndexPatterns = {
  base: 0,              // Default, base content
  dropdown: 1000,       // Dropdowns, popovers
  sticky: 300,          // Sticky headers, toolbars
  modal: 5100,          // Modals
  popover: 1100,        // Tooltips, popovers
  toast: 9500,          // Toast notifications
};

/**
 * Breakpoints
 * 
 * Standard responsive breakpoints matching Tailwind defaults.
 * Use these for consistent responsive behavior.
 */
export const breakpoints = {
  sm: '640px',          // Small devices (landscape phones)
  md: '768px',          // Medium devices (tablets)
  lg: '1024px',         // Large devices (desktops)
  xl: '1280px',         // Extra large devices
  '2xl': '1536px',      // 2X large devices (large desktops)
};

/**
 * Transitions
 * 
 * Standard transition timings and easings for consistent animations.
 */
export const transitions = {
  // Duration
  fast: '150ms',
  base: '200ms',
  slow: '300ms',
  slower: '500ms',
  
  // Easing
  easeIn: 'ease-in',
  easeOut: 'ease-out',
  easeInOut: 'ease-in-out',
  linear: 'linear',
  
  // Common transition combinations
  default: 'all 200ms ease-in-out',
  fast: 'all 150ms ease-in-out',
  slow: 'all 300ms ease-in-out',
  colors: 'background-color 200ms ease-in-out, color 200ms ease-in-out, border-color 200ms ease-in-out',
  transform: 'transform 200ms ease-in-out',
  opacity: 'opacity 200ms ease-in-out',
};

/**
 * Consolidated spacing export
 */
export const spacingTokens = {
  spacing,
  patterns: spacingPatterns,
  borderRadius,
  radiusPatterns,
  containers,
  containerPatterns,
  zIndexPatterns,
  breakpoints,
  transitions,
};

/**
 * Default export
 */
export default spacingTokens;
